import { useContext, useEffect, useRef } from 'react';

import { CentrifugeContext, CentrifugeEvent } from '@/components/__app/CentrifugeProvider';

type SubscriptionOptions = {
  /** Callback to be executed when the subscription is created
   * It may not get called if the subscription already exists,
   * so it shouldn't be relied on for critical logic like waiting
   * for the subscription to be ready before sending messages.
   */
  subscriptionCreatedCallback?: () => void;
  /** Callback to be executed when the subscription is ready */
  subscriptionReadyCallback?: () => void;
  enabled?: boolean;
};

const useChannelSubscription = (
  channel: string,
  callback: (data: unknown) => void,
  options?: SubscriptionOptions,
) => {
  const { addEventListener, removeEventListener, subscribeChannel, unsubscribeChannel, ready } =
    useContext(CentrifugeContext);
  const subscribed = useRef(false);

  useEffect(() => {
    if (!(options?.enabled ?? true)) {
      return;
    }

    const doSubscribe = () => {
      subscribeChannel(channel);
      subscribed.current = true;
    };

    const listener = (event: Event) => {
      if (!(event instanceof CentrifugeEvent)) {
        console.error('Invalid event received in channel subscription listener', event);
        return;
      }
      const centrifugeEvent = event;
      callback(centrifugeEvent.data);
    };

    addEventListener(`data:${channel}`, listener);

    const createListener = () => {
      options?.subscriptionCreatedCallback?.();
    };
    addEventListener(`subscribed:${channel}`, createListener);

    const channelReadyListener = () => {
      options?.subscriptionReadyCallback?.();
    };
    addEventListener(`ready:${channel}`, channelReadyListener);

    const onReady = () => {
      doSubscribe();
    };
    addEventListener('ready', onReady);
    if (ready.current) {
      doSubscribe();
    }

    return () => {
      removeEventListener('ready', onReady);
      removeEventListener(`data:${channel}`, listener);
      removeEventListener(`subscribed:${channel}`, createListener);
      removeEventListener(`ready:${channel}`, channelReadyListener);

      if (subscribed.current) {
        unsubscribeChannel(channel);
        subscribed.current = false;
      }
    };
  }, [
    channel,
    callback,
    options,
    unsubscribeChannel,
    subscribeChannel,
    ready,
    addEventListener,
    removeEventListener,
  ]);
};

export default useChannelSubscription;
